/**
 * Phase 2 (Task #56) — shared student display-name helper.
 *
 * Used by both the api-server and the classtrack web app to ensure the
 * same name appears everywhere a student is shown — list rows, exports,
 * AI prompt context, etc.
 *
 * Modes:
 *   - "full"     → "First Last"           (falls back to "First" if no surname)
 *   - "first"    → "First"
 *   - "initials" → "F.L."                 (falls back to "F" if no surname)
 *   - "nickname" → "Preferred"            (falls back to first name)
 *   - "code"     → "STUDENT_CODE"         (falls back to initials, then first)
 *
 * `sequential` (Student 1, Student 2, …) is intentionally NOT handled here;
 * it requires a stable per-export id map and lives in the export layer.
 */

export type StudentDisplayMode =
  | "full"
  | "first"
  | "initials"
  | "nickname"
  | "code";

export const STUDENT_DISPLAY_MODES: ReadonlyArray<StudentDisplayMode> = [
  "full",
  "first",
  "initials",
  "nickname",
  "code",
] as const;

/**
 * Minimal shape of a student that the helper needs. Both Drizzle row types
 * and OpenAPI Student model satisfy this via structural typing.
 */
export interface DisplayableStudent {
  firstName?: string | null;
  lastName?: string | null;
  preferredName?: string | null;
  studentCode?: string | null;
  displayMode?: string | null;
}

const DEFAULT_MODE: StudentDisplayMode = "full";

function isDisplayMode(value: unknown): value is StudentDisplayMode {
  return (
    typeof value === "string" &&
    (STUDENT_DISPLAY_MODES as ReadonlyArray<string>).includes(value)
  );
}

/** Public type guard so callers can validate untrusted values (e.g. the
 *  loose JSONB profile.preferences bag) before passing them to the helper.
 */
export const isStudentDisplayMode = isDisplayMode;

/**
 * Resolve which display mode to use for a given student given the account
 * default. Per-student override wins over account default.
 */
export function getEffectiveDisplayMode(
  student: DisplayableStudent | null | undefined,
  accountDefault?: string | null,
): StudentDisplayMode {
  if (student && isDisplayMode(student.displayMode)) return student.displayMode;
  if (isDisplayMode(accountDefault)) return accountDefault;
  return DEFAULT_MODE;
}

function trimOrNull(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = v.trim();
  return t.length === 0 ? null : t;
}

function initialOf(v: string | null): string | null {
  if (!v) return null;
  // Take first non-whitespace character; uppercase for consistency.
  const ch = v.trim().charAt(0);
  return ch ? ch.toUpperCase() : null;
}

/**
 * Format a student's display name according to the resolved mode.
 *
 * Always returns a non-empty string. If the student has no firstName at
 * all, falls back to the supplied `unknownLabel` (default "Student").
 */
export function formatStudentName(
  student: DisplayableStudent | null | undefined,
  accountDefault?: string | null,
  unknownLabel: string = "Student",
): string {
  if (!student) return unknownLabel;

  const first = trimOrNull(student.firstName);
  const last = trimOrNull(student.lastName);
  const nickname = trimOrNull(student.preferredName);
  const code = trimOrNull(student.studentCode);
  const mode = getEffectiveDisplayMode(student, accountDefault);

  // Helpers used by several modes.
  const fullName = (): string => {
    if (first && last) return `${first} ${last}`;
    if (first) return first;
    if (last) return last;
    return unknownLabel;
  };

  const initialsName = (): string => {
    const fi = initialOf(first);
    const li = initialOf(last);
    if (fi && li) return `${fi}.${li}.`;
    if (fi) return `${fi}.`;
    if (li) return `${li}.`;
    return unknownLabel;
  };

  switch (mode) {
    case "full":
      return fullName();
    case "first":
      return first ?? last ?? unknownLabel;
    case "initials":
      return initialsName();
    case "nickname":
      return nickname ?? first ?? last ?? unknownLabel;
    case "code":
      if (code) return code;
      // Fall back through initials → first → unknown so a missing code
      // never leaks the full name unintentionally.
      return initialsName() === unknownLabel
        ? first ?? last ?? unknownLabel
        : initialsName();
  }
}

/**
 * Sort key for stable A→Z ordering regardless of mode. Always uses
 * lastName then firstName so list ordering doesn't shift around when
 * teachers change display preferences.
 */
export function studentSortKey(student: DisplayableStudent | null | undefined): string {
  if (!student) return "";
  const last = trimOrNull(student.lastName) ?? "";
  const first = trimOrNull(student.firstName) ?? "";
  return `${last.toLowerCase()}|${first.toLowerCase()}`;
}
